import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, Send } from "lucide-react";
import {
  enablePush,
  disablePush,
  getPermissionStatus,
  isPushSupported,
} from "@/utils/pushNotifications";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PushNotificationToggle = () => {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState("default");
  const [enabling, setEnabling] = useState(false);
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    (async () => {
      const ok = await isPushSupported();
      setSupported(ok);
      setPermission(getPermissionStatus());
      setHasToken(!!localStorage.getItem("fcm_token"));
    })();
  }, []);

  const handleEnable = async () => {
    setEnabling(true);
    const result = await enablePush();
    setEnabling(false);
    if (result.ok) {
      toast.success("Push notifications enabled");
      setPermission("granted");
      setHasToken(true);
    } else {
      toast.error(result.error || "Failed to enable push notifications");
      setPermission(getPermissionStatus());
    }
  };

  const handleDisable = async () => {
    await disablePush();
    setHasToken(false);
    toast.success("Push notifications disabled on this device");
  };

  const handleTest = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/notifications/test-push`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.sent > 0) {
        toast.success(`Test push sent to ${res.data.sent} device(s)`);
      } else {
        toast.error(res.data.reason === "no_tokens" ? "No devices registered yet" : "Failed to send test");
      }
    } catch (e) {
      toast.error("Failed to send test push");
    }
  };

  if (!supported) {
    return (
      <Card className="bg-card border-border" data-testid="push-card">
        <CardHeader>
          <CardTitle className="text-xl font-heading text-white flex items-center gap-2">
            <BellOff className="w-5 h-5 text-muted-foreground" /> Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Push notifications aren't supported on this browser. Try Chrome, Edge, or install this app to your home screen on iOS 16.4+.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border" data-testid="push-card">
      <CardHeader>
        <CardTitle className="text-xl font-heading text-white flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" /> Push Notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Get notified about workout reminders, streak warnings, and recovery suggestions — even when the app is closed.
        </p>

        {permission === "denied" ? (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
            <p className="text-sm text-red-400 font-medium">Notifications blocked</p>
            <p className="text-xs text-muted-foreground mt-1">
              Enable them in your browser's site settings, then return here to register your device.
            </p>
          </div>
        ) : !hasToken ? (
          <Button
            data-testid="enable-push-btn"
            onClick={handleEnable}
            disabled={enabling}
            className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider rounded-none"
          >
            <Bell className="w-4 h-4 mr-2" />
            {enabling ? "Enabling..." : "Enable Notifications"}
          </Button>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="test-push-btn"
              onClick={handleTest}
              variant="outline"
              className="border-primary/50 text-primary hover:bg-primary/10 rounded-none"
            >
              <Send className="w-4 h-4 mr-2" /> Send Test Push
            </Button>
            <Button
              data-testid="disable-push-btn"
              onClick={handleDisable}
              variant="ghost"
              className="text-white/40 hover:text-white rounded-none"
            >
              <BellOff className="w-4 h-4 mr-2" /> Turn Off
            </Button>
          </div>
        )}

        {hasToken && (
          <div className="flex items-center gap-2 mt-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-mono text-green-400 uppercase tracking-wider">
              Active on this device
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PushNotificationToggle;
